import React, { useState, useEffect } from 'react';
import { 
  FaCalendarAlt, FaUsers, FaChartLine, FaCheckCircle, FaClock, FaCalendarCheck,
  FaArrowUp, FaArrowDown, FaEye, FaUserCheck, FaChartBar, FaPlus, FaList,
  FaSync, FaExclamationTriangle, FaUser, FaBolt, FaArrowRight, FaMale, FaFemale,
  FaHeart, FaHeartbeat, FaTimes, FaClipboardCheck, FaUserTimes, FaRunning
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Legend, AreaChart, Area, LineChart, Line } from 'recharts';
import { getProgramDashboardStats, getProgramTrends, programService } from '../../../../firebase/programFirestoreService';
import { useUI } from '../../../../components/common/UIMessageProvider';
import { cache } from '../../../../utils/cacheManager';

interface ProgramTrend {
  label: string;
  value: number;
}

interface TopProgram {
  id: string;
  title: string;
  registrations: number;
  attendanceRate: number;
}

interface RecentRegistration {
  id: string;
  fullName: string;
  programTitle?: string;
  registrationStatus?: string;
  registrationDate: any;
  gender?: string;
  religion?: string;
  maritalStatus?: string;
  healthStatus?: string;
}

interface ProgramDashboardStats {
  totalPrograms?: number;
  publishedPrograms?: number;
  ongoingPrograms?: number;
  upcomingPrograms?: number;
  completedPrograms?: number;
  totalRegistrations?: number;
  totalAttended?: number;
  averageAttendanceRate?: number;
  programByCategory?: Record<string, number>;
  monthlyRegistrations?: { month: string; count: number }[];
  topPrograms?: TopProgram[];
  recentRegistrations?: RecentRegistration[];
  healthStatus?: Record<string, number>;
  male?: number;
  female?: number;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const CompactChart: React.FC<{ type: 'pie' | 'bar' | 'area' | 'line'; data: any; height?: number }> = ({ type, data, height = 180 }) => {
  if (!data) return null;

  if (type === 'pie') {
    // Support either rows or classic labels/datasets
    const pieData = data?.rows?.length
      ? (data.rows as any[]).map(r => ({ name: r.name || r.label, value: Number(r.value || 0) }))
      : ((data?.labels || []) as string[]).map((label: string, i: number) => ({ name: label, value: (data?.datasets?.[0]?.data || [])[i] || 0 }));

    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2}>
            {pieData.map((_, idx) => <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />)}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (type === 'bar') {
    // Support either rows (preferred) or classic labels/datasets
    const barData = data?.rows?.length
      ? (data.rows as any[]).map(r => ({ label: r.label, name: r.name || r.label, value: Number(r.value || 0) }))
      : ((data?.labels || []) as string[]).map((label: string, i: number) => ({ label, name: label, value: (data?.datasets?.[0]?.data || [])[i] || 0 }));

    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={barData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip formatter={(value: any, name: any, props: any) => [value, props?.payload?.name || name]} />
          <Bar dataKey="value" fill={COLORS[0]} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (type === 'area' || type === 'line') {
    const values = (data?.datasets?.[0]?.data || []) as number[];
    const labels = (data?.labels || []) as string[];
    const chartData = labels.map((label: string, i: number) => ({ name: label, value: values[i] || 0 }));

    return (
      <ResponsiveContainer width="100%" height={height}>
        {type === 'area' ? (
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS[0]} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={COLORS[0]} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Area type="monotone" dataKey="value" stroke={COLORS[0]} fillOpacity={1} fill="url(#colorValue)" />
          </AreaChart>
        ) : (
          <LineChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke={COLORS[0]} strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        )}
      </ResponsiveContainer>
    );
  }

  return null;
};

const MiniStatCard: React.FC<{ 
  label: string; 
  value: number | string; 
  icon: React.ComponentType<any>; 
  color: string;
  trend?: number;
}> = ({ label, value, icon: Icon, color, trend }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <p className="text-[10px] text-gray-500 font-medium uppercase">{label}</p>
        <p className="text-xl font-bold text-gray-900 mt-0.5">{value}</p>
        {trend !== undefined && (
          <span className={`text-[10px] font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className={`h-10 w-10 rounded-lg ${color} bg-opacity-10 flex items-center justify-center`}>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
    </div>
  </div>
);

const ProgramsDashboard: React.FC = () => {
  const ui = useUI();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ProgramDashboardStats | null>(null);
  const [trends, setTrends] = useState<ProgramTrend[]>([]);
  const [trendPeriod, setTrendPeriod] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');
  const [selectedProgram, setSelectedProgram] = useState<string>('all');
  const [programs, setPrograms] = useState<Array<{id: string; title: string}>>([]);

  // Derived stats: prefer aggregated `stats.male`/`stats.female` when available,
  // otherwise fall back to recentRegistrations sample.
  const genderStats = (() => {
    if (stats && (typeof (stats as any).male === 'number' || typeof (stats as any).female === 'number')) {
      return {
        male: (stats as any).male || 0,
        female: (stats as any).female || 0,
        other: 0
      } as Record<string, number>;
    }

    return stats?.recentRegistrations?.reduce((acc, reg) => {
      const gender = reg.gender?.toLowerCase() || 'other';
      acc[gender] = (acc[gender] || 0) + 1;
      return acc;
    }, {} as Record<string, number>) || {};
  })();

  const maritalStats = stats?.recentRegistrations?.reduce((acc, reg) => {
    const status = reg.maritalStatus?.toLowerCase() || 'unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  const healthLabelAbbrev = (k: string) => {
    const key = (k || 'unknown').toString().toLowerCase();
    const map: Record<string, string> = {
      active: 'Act',
      sick: 'Sick',
      partially_sick: 'P.Sick',
      partiallysick: 'P.Sick',
      disabled: 'Dis',
      other: 'Other',
      unknown: 'Unk'
    };
    return map[key] || key.substring(0, 3).toUpperCase();
  };

  const healthStats = (() => {
    // Prefer aggregated breakdown from stats if available
    if (stats && (stats as any).healthStatus && Object.keys((stats as any).healthStatus).length) {
      return Object.entries((stats as any).healthStatus).reduce((acc, [k, v]) => {
        const key = String(k || 'unknown').toLowerCase();
        acc[key] = Number(v || 0);
        return acc;
      }, {} as Record<string, number>);
    }

    // Fallback: derive from recent registrations
    if (stats?.recentRegistrations && stats.recentRegistrations.length) {
      return stats.recentRegistrations.reduce((acc, reg) => {
        const status = (reg.healthStatus || 'unknown').toString().toLowerCase();
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
    }

    return {};
  })();

  const statusStats = stats?.recentRegistrations?.reduce((acc, reg) => {
    const status = reg.registrationStatus?.toLowerCase() || 'unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  // Order health status rows so the bar chart shows: active, partially, disabled, other first
  const healthRows = (() => {
    const entries = Object.entries(healthStats || {});
    const normalize = (k: string) => {
      const s = (k || '').toString().toLowerCase();
       if (s.includes('active')) return 'active';
      if (s.includes('part')) return 'partially';
      if (s.includes('dis')) return 'disabled';
      if (s.includes('other')) return 'other';
      return s;
    };

    const orderMap: Record<string, number> = { active: 0, partially: 1, disabled: 2, other: 3 };

    return entries
      .sort((a, b) => {
        const na = normalize(a[0]);
        const nb = normalize(b[0]);
        const oa = Object.prototype.hasOwnProperty.call(orderMap, na) ? orderMap[na] : 99;
        const ob = Object.prototype.hasOwnProperty.call(orderMap, nb) ? orderMap[nb] : 99;
        if (oa !== ob) return oa - ob;
        return a[0].localeCompare(b[0]);
      })
      .map(([k, v]) => ({
        label: healthLabelAbbrev(k),
        name: (k || '').toString().replace(/_/g, ' ').replace(/(^|\s)\S/g, s => s.toUpperCase()),
        value: v
      }));
  })();

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const statsCacheKey = `programDashboard:${selectedProgram}`;
      const trendsCacheKey = `programTrends:${trendPeriod}:${selectedProgram}`;
      const ttl = 30 * 60 * 1000;

      if (selectedProgram && selectedProgram !== 'all') {
        // Load single-program stats
        const program = await programService.getProgram(selectedProgram);
        const programStats = await programService.getProgramStats(selectedProgram);
        const registrations = await programService.getProgramRegistrations(selectedProgram, 300).catch(() => [] as any[]);

        const computeMonthlyFromRegs = (regs: any[]) => {
          const map = new Map<string, { year: number; month: number; count: number }>();
          (regs || []).forEach(r => {
            try {
              const d = r.registrationDate?.toDate ? r.registrationDate.toDate() : new Date(r.registrationDate);
              if (!d || Number.isNaN(d.getTime())) return;
              const y = d.getFullYear();
              const m = d.getMonth();
              const key = `${y}-${m}`;
              const cur = map.get(key) || { year: y, month: m, count: 0 };
              cur.count += 1;
              map.set(key, cur);
            } catch {
              // ignore
            }
          });
          const arr = Array.from(map.values()).sort((a, b) => (a.year - b.year) || (a.month - b.month)).map(x => ({ month: new Date(x.year, x.month, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), count: x.count }));
          return arr;
        };

        const normalizedStats: ProgramDashboardStats = {
          totalPrograms: 1,
          publishedPrograms: program?.isPublished ? 1 : 0,
          ongoingPrograms: program?.status === 'ongoing' ? 1 : 0,
          upcomingPrograms: program?.status === 'published' ? 1 : 0,
          completedPrograms: program?.status === 'completed' ? 1 : 0,
          totalRegistrations: programStats?.total || registrations.length || 0,
          totalAttended: programStats?.attended || 0,
          averageAttendanceRate: programStats && programStats.total ? Math.round((programStats.attended / programStats.total) * 100) : 0,
          male: (typeof programStats?.male === 'number') ? programStats!.male : (registrations || []).filter((r: any) => (r.gender || '').toLowerCase() === 'male').length,
          female: (typeof programStats?.female === 'number') ? programStats!.female : (registrations || []).filter((r: any) => (r.gender || '').toLowerCase() === 'female').length,
          programByCategory: { [program?.category || 'Uncategorized']: 1 },
          monthlyRegistrations: (programStats && Array.isArray((programStats as any).monthlyRegistrations) && (programStats as any).monthlyRegistrations.length)
            ? (programStats as any).monthlyRegistrations.map((m: any) => ({ month: m.month || m.label || m.period || String(m.name || ''), count: Number(m.count || m.registrations || m.value || 0) }))
            : computeMonthlyFromRegs(registrations || []),
          topPrograms: program ? [{ id: program.id || '', title: program.title, registrations: programStats?.total || 0, attendanceRate: programStats && programStats.total ? Math.round((programStats.attended / programStats.total) * 100) : 0 }] : [],
          // Prefer healthStatus from programStats when available, otherwise derive from registrations
          healthStatus: (programStats && (programStats as any).healthStatus && Object.keys((programStats as any).healthStatus).length)
            ? (programStats as any).healthStatus
            : (registrations || []).reduce((acc: Record<string, number>, r: any) => {
                const s = (r.healthStatus || 'unknown').toString().toLowerCase();
                acc[s] = (acc[s] || 0) + 1;
                return acc;
              }, {}),
          recentRegistrations: (registrations || []).slice(0, 10).map(r => ({
            id: r.id ?? '',
            fullName: (r as any).fullName || '',
            programTitle: program?.title || '',
            registrationStatus: (r as any).registrationStatus || (r as any).registrationStatus || '',
            registrationDate: (r as any).registrationDate,
            religion: (r as any).religion || ''
          }))
        };

        setStats(normalizedStats);

        // trends: reuse existing getProgramTrends cache but it is global — get trends normally
        const trendsResult = await cache.get(trendsCacheKey, async () => {
          return await (getProgramTrends as any)(trendPeriod, selectedProgram === 'all' ? undefined : selectedProgram);
        }, ttl);

        if (trendsResult?.success && trendsResult.data) {
          const normalizedTrends: ProgramTrend[] = trendsResult.data.map((t: any) => ({
            label: t.period ?? t.label ?? '',
            value: typeof t.registrations === 'number' ? t.registrations : (t.value ?? 0)
          }));
          setTrends(normalizedTrends);
        }

      } else {
        const statsResult = await cache.get(statsCacheKey, async () => {
          return await (getProgramDashboardStats as any)();
        }, ttl);

        if (statsResult?.success && statsResult.data) {
          const normalizedStats = {
            ...statsResult.data,
            recentRegistrations: (statsResult.data.recentRegistrations || []).map((r: any) => ({
              ...r,
              id: r.id ?? '',
              religion: r.religion || ''
            }))
          } as ProgramDashboardStats;
          setStats(normalizedStats);

          // Ensure aggregated male/female totals exist for the "All Programs" view.
          // Try to read from per-program stats (cached), falling back to fetching
          // registrations for programs that lack gender breakdown.
          (async () => {
            try {
              const genderCacheKey = `programGenderTotals:all`;
              const totals = await cache.get(genderCacheKey, async () => {
                const list = await programService.getPrograms(500).catch(() => [] as any[]);
                const ids = (list || []).map((p: any) => p.id).filter((id: any) => !!id);

                let male = 0;
                let female = 0;
                const needRegistrations: any[] = [];

                const statsResults = await Promise.allSettled(ids.map((id: any) => programService.getProgramStats(id)));
                statsResults.forEach((res, idx) => {
                  if (res.status === 'fulfilled' && res.value) {
                    const v: any = res.value;
                    if (typeof v.male === 'number' || typeof v.female === 'number') {
                      male += Number(v.male || 0);
                      female += Number(v.female || 0);
                    } else {
                      needRegistrations.push(ids[idx]);
                    }
                  } else {
                    needRegistrations.push(ids[idx]);
                  }
                });

                if (needRegistrations.length) {
                  const regsResults = await Promise.allSettled(needRegistrations.map((id: any) => programService.getProgramRegistrations(id, 100).catch(() => [])));
                  regsResults.forEach(r => {
                    if (r.status === 'fulfilled' && Array.isArray(r.value)) {
                      (r.value as any[]).forEach(reg => {
                        const g = (reg.gender || '').toLowerCase();
                        if (g === 'male') male++;
                        else if (g === 'female') female++;
                      });
                    }
                  });
                }

                return { male, female };
              }, ttl);

              setStats(prev => prev ? ({ ...prev, male: totals.male, female: totals.female }) : ({ male: totals.male, female: totals.female } as ProgramDashboardStats));
            } catch (e) {
              // ignore aggregation errors
            }
          })();

          // Also aggregate health status across programs for the All view.
          (async () => {
            try {
              const healthCacheKey = `programHealthTotals:all`;
              const healthTotals = await cache.get(healthCacheKey, async () => {
                const list = await programService.getPrograms(500).catch(() => [] as any[]);
                const ids = (list || []).map((p: any) => p.id).filter((id: any) => !!id);

                const needRegistrations: any[] = [];
                const healthMap: Record<string, number> = {};

                const statsResults = await Promise.allSettled(ids.map((id: any) => programService.getProgramStats(id)));
                statsResults.forEach((res, idx) => {
                  if (res.status === 'fulfilled' && res.value) {
                    const v: any = res.value;
                    if (v.healthStatus && typeof v.healthStatus === 'object' && Object.keys(v.healthStatus).length) {
                      Object.entries(v.healthStatus).forEach(([k, val]) => {
                        const key = String(k || 'unknown').toLowerCase();
                        healthMap[key] = (healthMap[key] || 0) + Number(val || 0);
                      });
                    } else {
                      needRegistrations.push(ids[idx]);
                    }
                  } else {
                    needRegistrations.push(ids[idx]);
                  }
                });

                if (needRegistrations.length) {
                  const regsResults = await Promise.allSettled(needRegistrations.map((id: any) => programService.getProgramRegistrations(id, 100).catch(() => [])));
                  regsResults.forEach(r => {
                    if (r.status === 'fulfilled' && Array.isArray(r.value)) {
                      (r.value as any[]).forEach(reg => {
                        const h = (reg.healthStatus || 'unknown').toString().toLowerCase();
                        healthMap[h] = (healthMap[h] || 0) + 1;
                      });
                    }
                  });
                }

                return healthMap;
              }, ttl);

              setStats(prev => prev ? ({ ...prev, healthStatus: healthTotals }) : ({ healthStatus: healthTotals } as any));
            } catch (e) {
              // ignore
            }
          })();
        }

        const trendsResult = await cache.get(trendsCacheKey, async () => {
          return await (getProgramTrends as any)(trendPeriod);
        }, ttl);

        if (trendsResult?.success && trendsResult.data) {
          const normalizedTrends: ProgramTrend[] = trendsResult.data.map((t: any) => ({
            label: t.period ?? t.label ?? '',
            value: typeof t.registrations === 'number' ? t.registrations : (t.value ?? 0)
          }));
          setTrends(normalizedTrends);
        }
      }
    } catch (error) {
      ui.showMessage('Error loading dashboard', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [trendPeriod, selectedProgram]);

  useEffect(() => {
    const loadPrograms = async () => {
      try {
        const list = await programService.getPrograms(200);
        const normalized = (list || [])
          .filter((p: any) => p && (p.id || p.id === 0 || p.id === '') )
          .map((p: any) => ({ id: (p.id as string) || '', title: p.title || '' }));
        setPrograms(normalized);
      } catch (e) {
        // ignore
      }
    };
    loadPrograms();
  }, []);

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return 'Invalid';
    }
  };

  const formatReligion = (religion?: string) => {
    const value = (religion || '').toString().trim().toLowerCase();
    if (!value) return '-';
    if (value === 'christian') return 'Christian';
    if (value === 'muslim') return 'Muslim';
    if (value === 'other') return 'Other';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Compact Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Programs Dashboard</h1>
            <p className="text-xs text-gray-600">Overview & metrics</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={selectedProgram} onChange={(e) => setSelectedProgram(e.target.value)} className="px-2 py-1 border rounded text-xs">
              <option value="all">All Programs</option>
              {(programs.length ? programs : stats?.topPrograms || []).map(p => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
            <select value={trendPeriod} onChange={(e) => setTrendPeriod(e.target.value as any)} className="px-2 py-1 border rounded text-xs">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
            <button onClick={() => {
              try {
                cache.invalidate(`programDashboard:${selectedProgram}`);
                cache.invalidate(`programTrends:${trendPeriod}:${selectedProgram}`);
              } catch {}
              fetchDashboardData();
            }} className="px-2 py-1 bg-primary-600 text-white rounded text-xs hover:bg-primary-700">
              <FaSync className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Stats Grid - Ultra Compact */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
        <MiniStatCard label="Total" value={stats?.totalPrograms || 0} icon={FaCalendarAlt} color="text-blue-600" trend={12} />
        <MiniStatCard label="Published" value={stats?.publishedPrograms || 0} icon={FaCheckCircle} color="text-green-600" trend={8} />
        <MiniStatCard label="Ongoing" value={stats?.ongoingPrograms || 0} icon={FaRunning} color="text-purple-600" trend={5} />
        <MiniStatCard label="Upcoming" value={stats?.upcomingPrograms || 0} icon={FaClock} color="text-orange-600" />
        <MiniStatCard label="Completed" value={stats?.completedPrograms || 0} icon={FaClipboardCheck} color="text-gray-600" />
        <MiniStatCard label="Registrations" value={stats?.totalRegistrations || 0} icon={FaUsers} color="text-indigo-600" trend={24} />
        <MiniStatCard label="Attended" value={stats?.totalAttended || 0} icon={FaUserCheck} color="text-teal-600" />
        <MiniStatCard label="Attendance" value={`${stats?.averageAttendanceRate || 0}%`} icon={FaChartBar} color="text-pink-600" trend={3} />
        <MiniStatCard label="Male" value={genderStats.male || 0} icon={FaMale} color="text-blue-500" />
        <MiniStatCard label="Female" value={genderStats.female || 0} icon={FaFemale} color="text-pink-500" />
        <MiniStatCard label="Confirmed" value={statusStats.confirmed || 0} icon={FaCheckCircle} color="text-green-500" />
        <MiniStatCard label="Pending" value={statusStats.pending || 0} icon={FaClock} color="text-yellow-500" />
      </div>

      {/* Charts Grid - 4 Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Categories */}
        <div className="bg-white rounded-lg shadow-sm border p-3">
          <h3 className="text-xs font-semibold text-gray-700 mb-2">Categories</h3>
          {stats?.programByCategory && Object.keys(stats.programByCategory).length > 0 ? (
            <CompactChart type="pie" data={{
              labels: Object.keys(stats.programByCategory),
              datasets: [{ data: Object.values(stats.programByCategory) }]
            }} height={140} />
          ) : <div className="h-[140px] flex items-center justify-center text-xs text-gray-400">No data</div>}
        </div>

        {/* Monthly Trend */}
        <div className="bg-white rounded-lg shadow-sm border p-3">
          <h3 className="text-xs font-semibold text-gray-700 mb-2">Monthly Registrations</h3>
          {stats?.monthlyRegistrations?.length ? (
            <CompactChart type="area" data={{
              labels: stats.monthlyRegistrations.map(m => m.month),
              datasets: [{ data: stats.monthlyRegistrations.map(m => m.count) }]
            }} height={140} />
          ) : <div className="h-[140px] flex items-center justify-center text-xs text-gray-400">No data</div>}
        </div>

        {/* Gender Distribution */}
        <div className="bg-white rounded-lg shadow-sm border p-3">
          <h3 className="text-xs font-semibold text-gray-700 mb-2">Gender Distribution</h3>
          {Object.keys(genderStats).length > 0 ? (
            <CompactChart type="pie" data={{
              labels: Object.keys(genderStats).map(k => k.charAt(0).toUpperCase() + k.slice(1)),
              datasets: [{ data: Object.values(genderStats) }]
            }} height={140} />
          ) : <div className="h-[140px] flex items-center justify-center text-xs text-gray-400">No data</div>}
        </div>

        {/* Health Status */}
        <div className="bg-white rounded-lg shadow-sm border p-3">
          <h3 className="text-xs font-semibold text-gray-700 mb-2">Health Status</h3>
          {healthRows.length > 0 ? (
            <CompactChart type="bar" data={{ rows: healthRows }} height={140} />
          ) : <div className="h-[140px] flex items-center justify-center text-xs text-gray-400">No data</div>}
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top Programs */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border p-3">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Programs</h3>
          <div className="space-y-2">
            {stats?.topPrograms?.slice(0, 5).map((p, i) => (
              <div key={p.id} className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100">
                <div className="flex items-center gap-2">
                  <span className="h-6 w-6 rounded bg-primary-100 text-primary-800 flex items-center justify-center text-xs font-bold">
                    {i + 1}
                  </span>
                  <div>
                    <div className="text-xs font-medium text-gray-900">{p.title}</div>
                    <div className="text-[10px] text-gray-500">{p.registrations} registrations</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-gray-900">{p.attendanceRate}%</div>
                  <div className="text-[9px] text-gray-500">Attendance</div>
                </div>
              </div>
            )) || <div className="text-center py-6 text-xs text-gray-400">No programs</div>}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border p-3">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Actions</h3>
          <div className="space-y-2">
            {[
              { title: 'Create', icon: FaPlus, color: 'bg-blue-500', link: '/admin/programs/create' },
              { title: 'View All', icon: FaList, color: 'bg-green-500', link: '/admin/programs/list' },
              { title: 'Register', icon: FaUsers, color: 'bg-purple-500', link: '/admin/programs/register' },
              { title: 'Attendance', icon: FaUserCheck, color: 'bg-orange-500', link: '/admin/programs/attendance' }
            ].map((a, i) => (
              <Link key={i} to={a.link} className="flex items-center gap-2 p-2 rounded border hover:border-primary-300 hover:bg-primary-50 group">
                <div className={`h-8 w-8 rounded ${a.color} flex items-center justify-center text-white`}>
                  <a.icon className="h-4 w-4" />
                </div>
                <span className="text-xs font-medium text-gray-900 group-hover:text-primary-700">{a.title}</span>
                <FaArrowRight className="h-3 w-3 text-gray-400 ml-auto group-hover:text-primary-600" />
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Registrations - Compact Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="p-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Recent Registrations</h3>
          <Link to="/admin/programs/registrations" className="text-xs text-primary-600 hover:text-primary-700 font-medium">
            View All →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-700">NAME</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-700">PROGRAM</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-700">STATUS</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-700">RELIGION</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-700">DATE</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stats?.recentRegistrations?.slice(0, 5).map((r, i) => (
                <tr key={`${r.id}-${i}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center">
                        <FaUser className="h-3 w-3 text-gray-600" />
                      </div>
                      <span className="font-medium text-gray-900">{r.fullName}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-900">{r.programTitle || 'N/A'}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-medium ${
                      r.registrationStatus === 'confirmed' ? 'bg-green-100 text-green-800' :
                      r.registrationStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {r.registrationStatus}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-900">{formatReligion((r as any).religion)}</td>
                  <td className="px-3 py-2 text-gray-600">{formatDate(r.registrationDate)}</td>
                </tr>
              )) || <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">No registrations</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProgramsDashboard;
